import { useState } from 'react'
import { Button, Modal } from '@/components/ui'
import type { ShipConfirmationTracker } from '../api/types'

export interface LogEstimateModalProps {
  tracker: ShipConfirmationTracker | null
  isPending: boolean
  onCancel: () => void
  onSubmit: (body: { estimated_date?: string; note: string }) => void
}

/**
 * Inbound PO Shipping Confirmation Tracker — logs a vendor's revised (still
 * NOT firm) estimate without changing stage. This is the "still on the
 * water, new ETA" case — the exact KCC PO1862 failure mode, where several
 * date slips happened with no record of any of them. Every submission
 * appends its own stage_history entry.
 */
export function LogEstimateModal({ tracker, isPending, onCancel, onSubmit }: LogEstimateModalProps) {
  const [estimatedDate, setEstimatedDate] = useState('')
  const [note, setNote] = useState('')

  if (!tracker) return null

  const canSubmit = !!note.trim()

  function reset() {
    setEstimatedDate('')
    setNote('')
  }

  return (
    <Modal
      open={tracker !== null}
      onOpenChange={(next) => {
        if (!next) {
          reset()
          onCancel()
        }
      }}
      tone="info"
      title="Log estimate update"
      description={
        <>
          Record a vendor update for <strong>{tracker.odoo_po_name}</strong> without marking
          it confirmed — use this for a revised ETA that&apos;s still not a firm commitment.
        </>
      }
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              reset()
              onCancel()
            }}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={isPending}
            disabled={!canSubmit}
            onClick={() => onSubmit({ estimated_date: estimatedDate || undefined, note })}
          >
            Log update
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Revised estimate (optional)</span>
          <input
            type="date"
            value={estimatedDate}
            onChange={(e) => setEstimatedDate(e.target.value)}
            className="rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary focus:border-border-focus focus:outline-none"
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">What did the vendor say?</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. vendor says still awaiting vessel space"
            rows={3}
            className="rounded-xs border border-border-default bg-surface px-2 py-1.5 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
          />
        </label>
      </div>
    </Modal>
  )
}
